import { useState, useCallback } from "react";
import { GripVertical, ChevronUp, ChevronDown } from "lucide-react";
import { QuestionTooltip } from "./QuestionTooltip";

export interface RankingItem {
  value: string;
  label: string;
  rank: number;
}

export interface RankingQuestionType {
  id: string;
  type: "ranking";
  title: string;
  description?: string;
  tooltip?: string;
  required: boolean;
  options: { value: string; label: string }[];
}

interface RankingQuestionProps {
  question: RankingQuestionType;
  value: RankingItem[];
  onChange: (questionId: string, value: RankingItem[]) => void;
}

function initItems(
  options: { value: string; label: string }[],
  existingValue: RankingItem[]
): RankingItem[] {
  if (existingValue && existingValue.length === options.length) {
    // Restore saved order
    return [...existingValue].sort((a, b) => a.rank - b.rank);
  }
  // Default: use options order
  return options.map((opt, i) => ({ value: opt.value, label: opt.label, rank: i + 1 }));
}

export function RankingQuestion({ question, value, onChange }: RankingQuestionProps) {
  const [items, setItems] = useState<RankingItem[]>(() =>
    initItems(
      question.options,
      value && value.length ? value : []
    )
  );

  // Drag state
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const commit = useCallback(
    (newItems: RankingItem[]) => {
      const ranked = newItems.map((item, i) => ({ ...item, rank: i + 1 }));
      setItems(ranked);
      onChange(question.id, ranked);
    },
    [onChange, question.id]
  );

  // Arrow-based reordering (primary accessible method)
  const moveUp = (index: number) => {
    if (index === 0) return;
    const next = [...items];
    [next[index - 1], next[index]] = [next[index], next[index - 1]];
    commit(next);
  };

  const moveDown = (index: number) => {
    if (index === items.length - 1) return;
    const next = [...items];
    [next[index], next[index + 1]] = [next[index + 1], next[index]];
    commit(next);
  };

  // Drag-and-drop handlers
  const handleDragStart = (index: number) => setDragIndex(index);
  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverIndex(index);
  };
  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === dropIndex) {
      setDragIndex(null);
      setDragOverIndex(null);
      return;
    }
    const next = [...items];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(dropIndex, 0, moved);
    commit(next);
    setDragIndex(null);
    setDragOverIndex(null);
  };
  const handleDragEnd = () => {
    setDragIndex(null);
    setDragOverIndex(null);
  };

  // Touch support
  const [touchStartY, setTouchStartY] = useState<number | null>(null);
  const [touchIndex, setTouchIndex] = useState<number | null>(null);

  const handleTouchStart = (e: React.TouchEvent, index: number) => {
    setTouchStartY(e.touches[0].clientY);
    setTouchIndex(index);
  };

  const handleTouchEnd = (e: React.TouchEvent, index: number) => {
    if (touchStartY === null || touchIndex === null) return;
    const deltaY = e.changedTouches[0].clientY - touchStartY;
    const threshold = 40; // px per slot
    if (Math.abs(deltaY) >= threshold) {
      const slots = Math.round(deltaY / threshold);
      const targetIndex = Math.max(0, Math.min(items.length - 1, index + slots));
      if (targetIndex !== index) {
        const next = [...items];
        const [moved] = next.splice(index, 1);
        next.splice(targetIndex, 0, moved);
        commit(next);
      }
    }
    setTouchStartY(null);
    setTouchIndex(null);
  };

  return (
    <div className="space-y-6 w-full max-w-4xl mx-auto py-4">
      <div>
        <h2 className="text-2xl font-bold text-foreground mb-3">
          {question.title}
          {question.tooltip && <QuestionTooltip tooltip={question.tooltip} />}
        </h2>
        {question.description && (
          <p className="text-muted-foreground">{question.description}</p>
        )}
      </div>

      <p className="text-xs text-muted-foreground">
        Drag to reorder or use the arrows — rank 1 = most important
      </p>

      <div className="space-y-2 max-w-2xl">
        {items.map((item, index) => {
          const isDragging = dragIndex === index;
          const isDragTarget = dragOverIndex === index && dragIndex !== index;

          return (
            <div
              key={item.value}
              draggable
              onDragStart={() => handleDragStart(index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDrop={(e) => handleDrop(e, index)}
              onDragEnd={handleDragEnd}
              onTouchStart={(e) => handleTouchStart(e, index)}
              onTouchEnd={(e) => handleTouchEnd(e, index)}
              className={`
                flex items-center gap-3 p-3 rounded-md border transition-all select-none
                ${isDragging ? "opacity-40 border-primary bg-primary/5" : "border-border bg-card"}
                ${isDragTarget ? "border-primary border-2 bg-primary/10" : ""}
                hover:border-border/80 cursor-grab active:cursor-grabbing
              `}
            >
              {/* Rank badge */}
              <div className="flex-shrink-0 w-7 h-7 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-bold">
                {index + 1}
              </div>

              {/* Drag handle */}
              <GripVertical className="flex-shrink-0 h-4 w-4 text-muted-foreground" />

              {/* Label */}
              <span className="flex-1 text-sm font-medium text-foreground">{item.label}</span>

              {/* Arrow controls */}
              <div className="flex flex-col gap-0.5 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => moveUp(index)}
                  disabled={index === 0}
                  className="p-0.5 rounded hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  aria-label={`Move ${item.label} up`}
                >
                  <ChevronUp className="h-3.5 w-3.5 text-muted-foreground" />
                </button>
                <button
                  type="button"
                  onClick={() => moveDown(index)}
                  disabled={index === items.length - 1}
                  className="p-0.5 rounded hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  aria-label={`Move ${item.label} down`}
                >
                  <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
